import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DropdownOption } from '../../../../../../core/models/dropdown-option';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { DropdownService } from '../../../../../../core/services/resources/dropdown.service';
import { PublicSafetyCoordinatorCodeEnum } from '../../../../shared/constants/public-safety-coordinator-codes';
import { ApplicationService } from '../../../../shared/services/application/application.service';
import { ApplicationPageFacadeService } from '../../../shared/services/application-page-facade/application-page-facade.service';
import { PublicSafetyEwaFormOptionConstants } from '../public-safety-ewa-submit-to-ewa/constants/public-safety-ewa-form-option-constants';
import { SubmitToEwaFormOptionComponent } from '../shared/components/submit-to-ewa-form-option/submit-to-ewa-form-option.component';
import { SubmitToEwaFormSubmissionComponent } from '../shared/components/submit-to-ewa-form-submission/submit-to-ewa-form-submission.component';
import { SubmitToEwaPageBaseComponent } from '../shared/components/submit-to-ewa-page-base/submit-to-ewa-page-base.component';
import { EwaSubmissionResponse } from '../shared/models/ewa-submission-response';
import { SubmitToEwaDetailsResponse } from '../shared/models/submit-to-ewa-details-response';
import { SubmitToEwaFormOption } from '../shared/models/submit-to-ewa-form-option';
import { SubmitToEwaFormService } from '../shared/services/submit-to-ewa-form.service';
import { AdvisorPublicSafetySubmitToEwaFormModel } from './models/advisor-public-safety-submit-to-ewa-form-model';
import { AdvisorPublicSafetySubmitToEwaRequest } from './models/advisor-public-safety-submit-to-ewa-request';

@Component({
    selector: 'app-advisor-public-safety-submit-to-ewa',
    templateUrl: './advisor-public-safety-submit-to-ewa.component.html',
    styleUrl: '../shared/styles/submit-to-ewa.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatSelectModule,
        MatFormFieldModule,
        FormsModule,
        ReactiveFormsModule,
        MatIconModule,
        MatInputModule,
        MatButtonModule,
        SubmitToEwaFormOptionComponent,
        SubmitToEwaFormSubmissionComponent,
    ],
})
export class AdvisorPublicSafetySubmitToEwaComponent
    extends SubmitToEwaPageBaseComponent<
        AdvisorPublicSafetySubmitToEwaRequest,
        AdvisorPublicSafetySubmitToEwaFormModel
    >
    implements OnInit
{
    publicSafetyCoordinatorDropdownOptions: DropdownOption[] = [];

    readonly formServiceSelectionOptions: SubmitToEwaFormOption[] =
        this.createFormOptions();
    selectedFormOptionSubject: BehaviorSubject<SubmitToEwaFormOption | null> =
        new BehaviorSubject<SubmitToEwaFormOption | null>(null);
    selectedFormOption$: Observable<SubmitToEwaFormOption | null> =
        this.selectedFormOptionSubject.asObservable();

    constructor(
        protected override readonly route: ActivatedRoute,
        protected override readonly router: Router,
        protected override readonly applicationPageFacadeService: ApplicationPageFacadeService,
        private readonly submitToEwaService: SubmitToEwaFormService,
        private readonly applicationService: ApplicationService,
        private readonly dropdownService: DropdownService,
    ) {
        super(route, router, applicationPageFacadeService);
    }

    override form: FormGroup<
        FormGroupType<AdvisorPublicSafetySubmitToEwaFormModel>
    > = new FormGroup<FormGroupType<AdvisorPublicSafetySubmitToEwaFormModel>>({
        publicSafetyCoordinator: new FormControl(null),
    });

    override ngOnInit(): void {
        super.ngOnInit();
        this.loadDropdownData();
        this.loadFormListeners();
        this.setupFormValidators();
    }

    enableSubmission(): boolean {
        return this.form.valid;
    }

    /**
     * Submits the form to the EWA. Will do a modal popup to confirm the submission.
     * Then, will submit the form and redirect you to the dashboard.
     */
    handoverToEwaSubmission(): void {
        if (this.enableSubmission()) {
            this.submitToEwaService.handoverToEwaSubmission(
                () => this.saveFormDataToApi(this.generateRequestObject(true)),
                () => {
                    /**
                     * This is needed to prevent the guard from blocking the navigation
                     * since the submission has already been made and the user is being redirected.
                     */
                    this.hasChanges = false;
                },
            );
        } else {
            this.form.markAllAsTouched();
        }
    }

    /**
     * Handles the service selection from the form option cards.
     * @param formServiceSelectionOption The selected form option card.
     */
    handleServiceSelection(
        formServiceSelectionOption: SubmitToEwaFormOption,
    ): void {
        this.selectedFormOptionSubject.next(formServiceSelectionOption);
    }

    /**
     * Display the alternate public safety coordinator dropdown if the selected form option is the alternate public safety coordinator form option.
     * @returns Whether the alternate public safety coordinator dropdown should be displayed.
     */
    shouldDisplayAlternatePublicSafetyCoordinatorDropdown(): boolean {
        return (
            this.selectedFormOptionSubject.getValue() ===
            PublicSafetyEwaFormOptionConstants.useAlternatePublicSafetyCoordinatorFormOption
        );
    }

    protected override bindForm(response: SubmitToEwaDetailsResponse): void {
        this.form.patchValue(response);
    }

    protected override getFormDataFromApi(
        applicationId: string,
    ): Observable<SubmitToEwaDetailsResponse> {
        return this.applicationService.getDataForFinalSubmission(applicationId);
    }

    protected override saveFormDataToApi(
        request: AdvisorPublicSafetySubmitToEwaRequest,
    ): Observable<EwaSubmissionResponse> {
        return this.applicationService.finalSubmissionToEwa(request);
    }

    protected override generateRequestObject(
        isFinalSubmission: boolean = false,
    ): AdvisorPublicSafetySubmitToEwaRequest {
        const formValue = this.form
            .value as Required<AdvisorPublicSafetySubmitToEwaRequest>;

        return {
            ...formValue,
            submitToEwa: isFinalSubmission,
            applicationId: this.applicationId!,
        };
    }

    protected override postFormBindingActions(): void {
        this.loadDefaultSelectedFormOption();
    }

    private setupFormValidators(): void {
        this.form.controls.publicSafetyCoordinator.setValidators([
            Validators.required,
        ]);
        this.form.controls.publicSafetyCoordinator.updateValueAndValidity();
    }

    /**
     * Loads the default selected form option based on the public safety coordinator value from the API.
     */
    private loadDefaultSelectedFormOption(): void {
        const publicSafetyCoordinatorValue =
            this.form.controls.publicSafetyCoordinator.value;

        if (
            publicSafetyCoordinatorValue &&
            publicSafetyCoordinatorValue !==
                PublicSafetyCoordinatorCodeEnum.PSCA
        ) {
            this.handleServiceSelection(
                PublicSafetyEwaFormOptionConstants.useAlternatePublicSafetyCoordinatorFormOption,
            );
        } else {
            this.handleServiceSelection(
                PublicSafetyEwaFormOptionConstants.usePublicSafetyCoordinatorAssociatesFormOption,
            );

            this.form.controls.publicSafetyCoordinator.setValue(
                PublicSafetyCoordinatorCodeEnum.PSCA,
            );
        }
    }

    /**
     * Loads the dropdown data for the public safety coordinators.
     */
    private loadDropdownData() {
        this.dropdownService
            .getPublicSafetyCoordinators()
            .pipe(
                takeUntil(this.destroy$),
                tap((options: DropdownOption[]) => {
                    this.publicSafetyCoordinatorDropdownOptions =
                        options.filter(
                            (option) =>
                                option.value !==
                                PublicSafetyCoordinatorCodeEnum.PSCA,
                        );
                }),
            )
            .subscribe();
    }

    /**
     * Listens on the service selection form control for changes in order to update
     * the public safety coordinator.
     */
    private loadFormListeners() {
        this.selectedFormOption$
            .pipe(takeUntil(this.destroy$))
            .subscribe((selectedOption) => {
                if (
                    selectedOption ===
                    PublicSafetyEwaFormOptionConstants.usePublicSafetyCoordinatorAssociatesFormOption
                ) {
                    this.form.controls.publicSafetyCoordinator.setValue(
                        PublicSafetyCoordinatorCodeEnum.PSCA,
                    );

                    this.form.controls.publicSafetyCoordinator.markAsDirty();
                    this.form.updateValueAndValidity();
                } else if (
                    selectedOption ===
                        PublicSafetyEwaFormOptionConstants.useAlternatePublicSafetyCoordinatorFormOption &&
                    this.form.controls.publicSafetyCoordinator.value ===
                        PublicSafetyCoordinatorCodeEnum.PSCA
                ) {
                    this.form.controls.publicSafetyCoordinator.setValue(null);
                }
            });
    }

    /**
     * Creates the form options for the form option cards.
     * @returns List of form options for the form option cards.
     */
    private createFormOptions(): SubmitToEwaFormOption[] {
        return PublicSafetyEwaFormOptionConstants.formOptions();
    }
}
